/**
 * RecordingProvider: o estado da gravação da chamada, dentro da sala LiveKit (docs/specs/otto/RECORDING.md §4).
 *
 * O ESTADO vem da sala (o metadado do LiveKit, `parseRoomRecording`), igual para o atendente e o convidado e ao vivo. O que
 * cada lado FAZ com ele é diferente e o provedor recebe só o que o seu lado precisa:
 *
 * - `agent` (a sala de quem criou a chamada): lê o painel (cota do mês, se o plano grava), e oferece pedir e parar;
 * - `guest` (a página pública): sabe se precisa responder ao pedido (ou ao aviso de que a sala já grava) e responde.
 *
 * Fica ACIMA de onde a sala é recriada (ida e volta da janela flutuante), como o da transcrição, para o erro e a resposta
 * do convidado não se perderem nessa troca.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoomInfo } from '@livekit/components-react';
import { RecordingContext } from './recordingContext';
import { ROOM_RECORDING_STATE, parseRoomRecording } from './recordingProtocol';
import { GUEST_CONSENT, recordingErrorText } from './recordingCopy';

/**
 * @param {{
 *   agent?: { videoCallId: string, api: { getPanel: Function, request: Function, stop: Function } },
 *   guest?: { token: string, acceptedAtJoin?: boolean, api: { respond: Function }, onLeave?: () => void },
 *   children: React.ReactNode,
 * }} props
 */
export function RecordingProvider({ agent, guest, children }) {
    const { metadata } = useRoomInfo();
    const room = useMemo(() => parseRoomRecording(metadata), [metadata]);
    const agentState = useAgentRecording(agent, room.state);
    const guestState = useGuestRecording(guest, room);

    const value = useMemo(() => ({
        state: room.state,
        requestId: room.requestId,
        recordingId: room.recordingId,
        startedAt: room.startedAt,
        agent: agentState,
        guest: guestState,
    }), [room, agentState, guestState]);

    return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>;
}

/** O lado do atendente: o painel (cota, plano), pedir e parar. `null` quando o provedor não é do atendente. */
function useAgentRecording(agent, roomState) {
    const videoCallId = agent?.videoCallId;
    const api = agent?.api;
    const [panel, setPanel] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const loadPanel = useCallback(async () => {
        if (!videoCallId) return;
        try {
            setPanel(await api.getPanel(videoCallId));
        } catch {
            // Sem o painel o botão simplesmente não aparece: ele só existe quando se sabe o que o plano permite
            setPanel(null);
        }
    }, [videoCallId, api]);

    useEffect(() => { loadPanel(); }, [loadPanel]);

    // A gravação acabou (parou, o Egress falhou, a chamada terminou): a cota do mês mudou, relê
    const previous = useRef(roomState);
    useEffect(() => {
        if (previous.current === ROOM_RECORDING_STATE.RECORDING && roomState !== ROOM_RECORDING_STATE.RECORDING) loadPanel();
        previous.current = roomState;
    }, [roomState, loadPanel]);

    const run = useCallback(async (action) => {
        setBusy(true);
        setError(null);
        try {
            await action();
        } catch (err) {
            setError(recordingErrorText(err));
        } finally {
            setBusy(false);
        }
        // Qualquer resultado (inclusive o erro) pode ter mudado a cota: o servidor é quem sabe
        loadPanel();
    }, [loadPanel]);

    const request = useCallback(() => run(() => api.request(videoCallId)), [run, api, videoCallId]);
    const stop = useCallback(() => run(() => api.stop(videoCallId)), [run, api, videoCallId]);
    const clearError = useCallback(() => setError(null), []);

    const allowance = panel?.allowance ?? null;
    return useMemo(() => (agent ? {
        enabled: panel?.enabled === true,
        canControl: panel?.canControl === true,
        available: allowance?.available === true,
        reason: allowance?.reason ?? null,
        used: allowance?.used ?? 0,
        limit: allowance?.limit ?? null,
        unlimited: allowance?.unlimited === true,
        lastOne: allowance?.lastOne === true,
        busy,
        error,
        clearError,
        request,
        stop,
    } : null), [agent, panel, allowance, busy, error, clearError, request, stop]);
}

/** O lado do convidado: precisa responder? e a resposta. `null` quando o provedor não é do convidado. */
function useGuestRecording(guest, room) {
    const [accepted, setAccepted] = useState(guest?.acceptedAtJoin === true);
    const [answeredRequest, setAnsweredRequest] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const token = guest?.token;
    const respond = guest?.api?.respond;
    const onLeave = guest?.onLeave;

    // Pedido pendente que eu ainda não respondi; ou a sala já grava e eu ainda não aceitei (cheguei no meio, ou a janela recarregou)
    const needsAnswer = Boolean(guest) && !accepted && (
        (room.state === ROOM_RECORDING_STATE.REQUESTED && answeredRequest !== room.requestId)
        || room.state === ROOM_RECORDING_STATE.RECORDING
    );
    const kind = room.state === ROOM_RECORDING_STATE.RECORDING ? ROOM_RECORDING_STATE.RECORDING : ROOM_RECORDING_STATE.REQUESTED;

    const answer = useCallback(async (accept) => {
        setBusy(true);
        setError(null);
        try {
            await respond({ accepted: accept, roomToken: token });
            if (accept) setAccepted(true);
            else if (kind === ROOM_RECORDING_STATE.RECORDING) onLeave?.();
            setAnsweredRequest(room.requestId);
        } catch {
            setError(GUEST_CONSENT.error);
        } finally {
            setBusy(false);
        }
    }, [respond, token, kind, onLeave, room.requestId]);

    return useMemo(() => (guest ? { needsAnswer, kind, busy, error, answer } : null), [guest, needsAnswer, kind, busy, error, answer]);
}

export default RecordingProvider;
